import { Component, inject, input, OnInit, output, signal } from '@angular/core';
import { CoreModule, dateAfterControlValidator, dateTodayOrAfterValidator, emailValidator, FormComponent, OffersType, phoneValidator } from '@toevla-webapplications/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { BookingService } from '../../../../services/booking/booking.service';
import { TranslateModule } from '@ngx-translate/core';
import { ToastsService } from '../../../../services/toasts/toasts.service';
import { Offer } from '../../../../services/offers/offer.model';

@Component({
	selector: 'ivva-groups-booking',
	templateUrl: './groups-booking.component.html',
	styleUrls: ['./groups-booking.component.scss'],
	imports: [FormsModule, ReactiveFormsModule, CoreModule, TranslateModule],
})
export class GroupsBookingComponent extends FormComponent implements OnInit {
	offer = input<Offer>();
	submitted = output();

	loadingSubmit = signal(false);

	id: string;
	hourOptions: string[];

	readonly MAX_CHARS_INFO = 200;
	readonly MIN_PARTICIPANTS = 5;
	readonly OffersType = OffersType;

	readonly #bookingService = inject(BookingService);
	readonly #fb = inject(FormBuilder);
	readonly #toastsService = inject(ToastsService);

	get confirmThresholdError(): boolean {
		return this.showErrors && !!this.form?.get('confirmThreshold')?.errors;
	}

	get other(): FormArray {
		return this.form?.get('other') as FormArray;
	}

	ngOnInit() {
		this.hourOptions = this.generateHourOptions();

		const offer = this.offer();

		if (offer) {
			this.#bookingService.new(offer, true);
			this.form = this.createForm();
		}
	}

	addContact(o?: any) {
		const other = this.#fb.group({
			fullName: [o?.fullName, Validators.required],
			email: [o?.email, [Validators.required, emailValidator]],
		});

		this.other.push(other);
	}

	removeContact(index: number) {
		this.other.removeAt(index);
	}

	submit() {
		const data = this.getFormData();
		this.#bookingService.update(data);

		const valid = this.isFormValid();

		if (!this.loadingSubmit() && valid) {
			this.loadingSubmit.set(true);

			this.#bookingService.submit().subscribe({
				next: () => {
					this.loadingSubmit.set(false);
					this.submitted.emit();
				},
				error: (res) => {
					this.#toastsService.serverError(res);
					this.loadingSubmit.set(false);
				},
			});
		}
	}

	private createForm() {
		const group: FormGroup<any> = this.#fb.group({
			socialOrganization: this.#fb.group({
				name: [null, Validators.required],
				address: [null, Validators.required],
				postalCode: [null, Validators.required],
				city: [null, Validators.required],
			}),
			booker: this.#fb.group({
				firstName: [null, Validators.required],
				lastName: [null, Validators.required],
				email: [null, [Validators.required, emailValidator]],
				phone: [null, [Validators.required, phoneValidator]],
			}),
			other: this.#fb.array([]),
			arrivalDate: [null, [Validators.required, dateTodayOrAfterValidator]],
			arrivalHour: [null],
			participants: [null, [Validators.required, Validators.min(this.MIN_PARTICIPANTS)]],
			extraInfo: [null, Validators.maxLength(this.MAX_CHARS_INFO)],
			confirmThreshold: [false, Validators.requiredTrue],
		});

		if (this.offer()?.type === OffersType.ACCOMMODATION) {
			group.addControl(
				'departureDate',
				this.#fb.control(null, [Validators.required, dateTodayOrAfterValidator, dateAfterControlValidator(group.get('arrivalDate') as any, true, true)])
			);
			this.controlShouldTriggerValidation('arrivalDate', 'departureDate', group);
		}

		return group;
	}

	private generateHourOptions(): string[] {
		const list = [];

		for (let hour = 0; hour < 24; hour++) {
			const h = hour < 10 ? '0' + hour : hour;
			list.push(`${h}:00`);
			list.push(`${h}:30`);
		}

		return list;
	}
}
